import { useRouter } from "expo-router";
import { FlatList, StyleSheet } from "react-native";
import { HomeSection, UnregisteredPlaceholder } from "../../src/components";
import { useAppSelector } from "../../src/hooks";

import { Property, isWeb } from "@stepapp/core";
import { Button, InfoBlock, Typography, spacing } from "@stepapp/ui";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { AppStorage } from "../../src/services/AppStorage";

const keyExtractor = ({ propertyId }: Property) => propertyId;

const MyHomeScreen = () => {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const isAuthenticated = !!AppStorage().get("token");
  const properties = useAppSelector(({ user }) => user?.properties);

  const ListHeaderComponent = useCallback(
    () => <Typography variant="newH2">{t("screens.myHome.title")}</Typography>,
    [t],
  );

  const navigateToEditProperty = useCallback(() => {
    navigate("/my-home/edit");
  }, [navigate]);

  const ListEmptyComponent = useCallback(
    () =>
      isAuthenticated ? (
        <InfoBlock
          buttonLabel={t("screens.myHome.addInfo")}
          description={t("screens.myHome.addressNote")}
          onPress={navigateToEditProperty}
        />
      ) : (
        <UnregisteredPlaceholder description={t("screens.myHome.notRegistered")} />
      ),
    [isAuthenticated, navigateToEditProperty, t],
  );

  const ListFooterComponent = useCallback(
    () =>
      isAuthenticated && (
        <Button
          containerStyle={styles.addAddressButton}
          disabled={properties.length >= 10}
          label={t("screens.homeEdit.addAddress")}
          onPress={navigateToEditProperty}
          type="primary"
        />
      ),
    [properties.length, navigateToEditProperty, t, isAuthenticated],
  );

  const renderItem = useCallback(
    ({ item }: { item: Property }) => (
      <HomeSection key={item.propertyId} isHomeEditScreen property={item} />
    ),
    [],
  );

  return (
    <FlatList
      ListHeaderComponent={ListHeaderComponent}
      ListFooterComponent={ListFooterComponent}
      ListEmptyComponent={ListEmptyComponent}
      contentContainerStyle={styles.container}
      data={properties}
      keyExtractor={keyExtractor}
      renderItem={renderItem}
    />
  );
};

export default MyHomeScreen;

const styles = StyleSheet.create({
  addAddressButton: {
    paddingLeft: spacing.unit * 4.5,
    paddingRight: spacing.unit * 4.5,
    marginBottom: spacing.unit * 20,
    marginTop: spacing.unit * 4,
  },
  container: {
    marginTop: spacing.unit * (isWeb ? 4 : 2),
  },
});
